import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { LogOut, Trash2, Plus, Send, Upload, Inbox, CalendarOff } from "lucide-react";
import { api, imgSrc, formatApiError } from "../lib/api";
import { CakeMark } from "../components/Logo";

const STATUSES = ["Pending", "Accepted", "In Preparation", "Ready", "Completed", "Cancelled"];
const EMAIL_STATUSES = ["Accepted", "Ready", "Completed"];
const STATUS_STYLES = {
  Pending: "bg-[#C5A059]/15 text-[#C5A059] border-[#C5A059]/40",
  Accepted: "bg-[#7A8B78]/15 text-[#7A8B78] border-[#7A8B78]/40",
  "In Preparation": "bg-[#B87D8E]/15 text-[#B87D8E] border-[#B87D8E]/40",
  Ready: "bg-[#8B5D6C]/15 text-[#B87D8E] border-[#B87D8E]/40",
  Completed: "bg-[#7A8B78]/20 text-[#7A8B78] border-[#7A8B78]/50",
  Cancelled: "bg-red-500/10 text-red-400 border-red-400/30",
};

const TABS = [
  { id: "orders", label: "Orders" },
  { id: "messages", label: "Messages" },
  { id: "gallery", label: "Gallery" },
  { id: "pricing", label: "Pricing" },
  { id: "availability", label: "Availability" },
];

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
};

const OrdersTab = ({ orders, reload }) => {
  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/orders/${id}`, { status });
      toast.success(
        EMAIL_STATUSES.includes(status)
          ? `Order marked as ${status} — customer emailed`
          : `Order marked as ${status}`
      );
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (orders.length === 0)
    return (
      <div className="text-center py-24 text-[#F9F6F0]/40" data-testid="orders-empty">
        <Inbox className="mx-auto mb-4" size={32} />
        <p className="font-serif text-2xl">No orders yet</p>
        <p className="text-sm mt-2">New order requests from the website will appear here.</p>
      </div>
    );

  return (
    <div className="space-y-4" data-testid="orders-list">
      {orders.map((o) => (
        <motion.div
          key={o.id}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#281D22] border border-[#B87D8E]/15 rounded-2xl p-6"
          data-testid={`order-card-${o.id}`}
        >
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="font-serif text-2xl text-[#F9F6F0]">{o.name}</p>
              <p className="text-sm text-[#B87D8E] mt-1">
                {o.occasion} · {fmtDate(o.event_date)} · {o.servings}
              </p>
            </div>
            <select
              value={o.status}
              onChange={(e) => setStatus(o.id, e.target.value)}
              data-testid={`order-status-select-${o.id}`}
              className={`text-xs font-medium tracking-wide px-4 py-2 rounded-full border bg-transparent cursor-pointer ${STATUS_STYLES[o.status] || STATUS_STYLES.Pending}`}
            >
              {STATUSES.map((s) => (
                <option key={s} value={s} className="bg-[#281D22] text-[#F9F6F0]">
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm text-[#F9F6F0]/60">
            <span>Flavour: <span className="text-[#F9F6F0]">{o.flavour}</span></span>
            <span>Email: <span className="text-[#F9F6F0]">{o.email}</span></span>
            <span>Phone: <span className="text-[#F9F6F0]">{o.phone}</span></span>
          </div>
          {o.dietary && <p className="mt-3 text-sm text-[#C5A059]">Dietary: {o.dietary}</p>}
          {o.extras?.length > 0 && <p className="mt-2 text-sm text-[#F9F6F0]/60">Finishing touches: {o.extras.join(", ")}</p>}
          {o.estimate && <p className="mt-2 text-sm text-[#C5A059]">Customer estimate: {o.estimate}</p>}
          {o.notes && <p className="mt-3 text-sm text-[#F9F6F0]/60 italic">“{o.notes}”</p>}
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#F9F6F0]/30">
              Received {fmtDate(o.created_at)}
              {EMAIL_STATUSES.includes(o.status) && " · status email sent to customer"}
            </p>
            {o.track_token && (
              <button
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/track/${o.track_token}`);
                  toast.success("Tracking link copied — share it with the customer");
                }}
                data-testid={`order-track-copy-${o.id}`}
                className="text-[10px] uppercase tracking-[0.2em] text-[#C5A059] border border-[#C5A059]/40 rounded-full px-3 py-1.5 hover:bg-[#C5A059]/10 transition-colors"
              >
                Copy tracking link
              </button>
            )}
          </div>
        </motion.div>
      ))}
    </div>
  );
};

const MessagesTab = ({ conversations, reload }) => {
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  const openConvo = async (id) => {
    try {
      const res = await api.get(`/admin/conversations/${id}`);
      setActive(res.data);
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!reply.trim() || !active) return;
    setSending(true);
    try {
      await api.post(`/admin/conversations/${active.id}/reply`, { message: reply.trim() });
      setReply("");
      openConvo(active.id);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-5" data-testid="messages-panel">
      <div className="lg:col-span-4 space-y-3">
        {conversations.length === 0 && (
          <div className="text-center py-20 text-[#F9F6F0]/40" data-testid="messages-empty">
            <Inbox className="mx-auto mb-4" size={32} />
            <p className="font-serif text-2xl">No messages yet</p>
            <p className="text-sm mt-2">Customer chat messages will land here.</p>
          </div>
        )}
        {conversations.map((c) => (
          <button
            key={c.id}
            onClick={() => openConvo(c.id)}
            data-testid={`conversation-item-${c.id}`}
            className={`w-full text-left bg-[#281D22] border rounded-2xl p-5 transition-colors ${
              active?.id === c.id ? "border-[#C5A059]/60" : "border-[#B87D8E]/15 hover:border-[#B87D8E]/40"
            }`}
          >
            <div className="flex items-center justify-between gap-3">
              <p className="font-serif text-xl text-[#F9F6F0]">{c.name}</p>
              {c.unread_admin && <span className="w-2.5 h-2.5 rounded-full bg-[#C5A059] shrink-0" data-testid={`unread-dot-${c.id}`} />}
            </div>
            <p className="text-xs text-[#F9F6F0]/50 mt-1 truncate">{c.last_message}</p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#F9F6F0]/30 mt-3">{fmtDate(c.updated_at)} · {c.message_count} messages</p>
          </button>
        ))}
      </div>

      <div className="lg:col-span-8 bg-[#281D22] border border-[#B87D8E]/15 rounded-2xl flex flex-col min-h-[420px]" data-testid="conversation-thread">
        {!active ? (
          <div className="flex-1 flex items-center justify-center text-[#F9F6F0]/30 font-serif text-xl p-10 text-center">
            Choose a conversation to read &amp; reply
          </div>
        ) : (
          <>
            <div className="px-6 py-4 border-b border-[#B87D8E]/15">
              <p className="font-serif text-xl text-[#F9F6F0]">{active.name}</p>
              <p className="text-xs text-[#F9F6F0]/50 mt-0.5">{active.contact}</p>
            </div>
            <div className="flex-1 p-6 space-y-3 overflow-y-auto max-h-[380px]">
              {active.messages.map((m) => (
                <div key={m.id} className={`flex ${m.sender === "owner" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                      m.sender === "owner"
                        ? "bg-[#8B5D6C] text-[#F9F6F0] rounded-br-sm"
                        : "bg-[#F9F6F0]/5 text-[#F9F6F0]/90 border border-[#B87D8E]/20 rounded-bl-sm"
                    }`}
                  >
                    {m.text}
                  </div>
                </div>
              ))}
            </div>
            <form onSubmit={send} className="p-4 border-t border-[#B87D8E]/15 flex gap-2">
              <input
                className="field-dark flex-1"
                placeholder={`Reply to ${active.name}…`}
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                data-testid="admin-reply-input"
              />
              <button
                type="submit"
                disabled={sending}
                data-testid="admin-reply-button"
                aria-label="Send reply"
                className="shrink-0 w-11 h-11 rounded-full bg-[#8B5D6C] text-[#F9F6F0] flex items-center justify-center hover:bg-[#4A2834] disabled:opacity-60 transition-colors"
              >
                <Send size={16} />
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

const GalleryTab = ({ gallery, reload }) => {
  const [form, setForm] = useState({ title: "", category: "Celebration", description: "", imageUrl: "" });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let storage_path = "";
      if (file) {
        const data = new FormData();
        data.append("file", file);
        const up = await api.post("/admin/upload", data);
        storage_path = up.data.path;
      }
      await api.post("/admin/gallery", {
        title: form.title,
        category: form.category,
        description: form.description,
        image_url: form.imageUrl,
        storage_path,
      });
      toast.success("Added to the gallery");
      setForm({ title: "", category: "Celebration", description: "", imageUrl: "" });
      setFile(null);
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/gallery/${id}`);
      toast.success("Removed from the gallery");
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="gallery-manager">
      <form onSubmit={add} className="lg:col-span-4 bg-[#281D22] border border-[#B87D8E]/15 rounded-2xl p-6 space-y-4 h-fit" data-testid="gallery-upload-form">
        <p className="font-serif text-2xl text-[#F9F6F0]">Add a new bake</p>
        <input className="field-dark" required placeholder="Title — e.g. Blush Rose Wedding Tier" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="gallery-title-input" />
        <select className="field-dark" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="gallery-category-select">
          {["Wedding", "Birthday", "Celebration", "Cupcakes", "Dessert Tables"].map((c) => (
            <option key={c} className="bg-[#281D22]">{c}</option>
          ))}
        </select>
        <textarea className="field-dark min-h-[80px] resize-none" placeholder="Short description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="gallery-description-input" />
        <label className="flex items-center justify-center gap-3 border border-dashed border-[#B87D8E]/30 rounded-xl py-6 cursor-pointer text-sm text-[#F9F6F0]/60 hover:border-[#C5A059]/60 transition-colors" data-testid="gallery-file-drop">
          <Upload size={16} className="text-[#C5A059]" />
          {file ? file.name : "Upload a photo"}
          <input type="file" accept="image/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="gallery-file-input" />
        </label>
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#F9F6F0]/30 text-center">or paste an image link</p>
        <input className="field-dark" placeholder="https://…" value={form.imageUrl} onChange={(e) => setForm({ ...form, imageUrl: e.target.value })} data-testid="gallery-url-input" />
        <button
          type="submit"
          disabled={saving || (!file && !form.imageUrl)}
          data-testid="gallery-add-button"
          className="w-full py-3.5 rounded-full bg-[#8B5D6C] text-[#F9F6F0] text-sm font-medium border border-[#C5A059]/60 hover:bg-[#4A2834] disabled:opacity-50 transition-colors"
        >
          {saving ? "Adding…" : "Add to gallery"}
        </button>
      </form>

      <div className="lg:col-span-8 grid grid-cols-2 md:grid-cols-3 gap-4">
        {gallery.map((item) => (
          <div key={item.id} className="relative group rounded-2xl overflow-hidden aspect-square border border-[#B87D8E]/15" data-testid={`admin-gallery-item-${item.id}`}>
            <img src={imgSrc(item)} alt={item.title} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-[#1C1417]/80 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-3 p-4 text-center">
              <p className="font-serif text-lg text-[#F9F6F0]">{item.title}</p>
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#C5A059]">{item.category}</p>
              <button onClick={() => remove(item.id)} data-testid={`gallery-delete-${item.id}`} className="mt-2 inline-flex items-center gap-2 text-xs text-red-300 border border-red-400/40 rounded-full px-4 py-2 hover:bg-red-500/10 transition-colors">
                <Trash2 size={13} /> Remove
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const PricingTab = ({ pricing, reload }) => {
  const [sections, setSections] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (pricing) setSections(JSON.parse(JSON.stringify(pricing.sections)));
  }, [pricing]);

  if (!sections) return null;

  const updateItem = (si, ii, key, value) =>
    setSections((s) => s.map((sec, i) => (i === si ? { ...sec, items: sec.items.map((it, j) => (j === ii ? { ...it, [key]: value } : it)) } : sec)));

  const removeItem = (si, ii) =>
    setSections((s) => s.map((sec, i) => (i === si ? { ...sec, items: sec.items.filter((_, j) => j !== ii) } : sec)));

  const addItem = (si) =>
    setSections((s) =>
      s.map((sec, i) =>
        i === si ? { ...sec, items: [...sec.items, { id: `item-${Date.now()}`, name: "New item", price: "£0" }] } : sec
      )
    );

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/pricing", { sections });
      toast.success("Pricing updated — live on the site now");
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="pricing-manager">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {sections.map((section, si) => (
          <div key={section.id} className="bg-[#281D22] border border-[#B87D8E]/15 rounded-2xl p-6" data-testid={`pricing-section-${section.id}`}>
            <p className="font-serif text-2xl text-[#F9F6F0] mb-5">{section.title}</p>
            <div className="space-y-3">
              {section.items.map((item, ii) => (
                <div key={item.id} className="flex items-center gap-2">
                  <input className="field-dark flex-1 !py-2.5 text-xs" value={item.name} onChange={(e) => updateItem(si, ii, "name", e.target.value)} data-testid={`pricing-name-${item.id}`} />
                  <input className="field-dark !w-28 !py-2.5 text-xs" value={item.price} onChange={(e) => updateItem(si, ii, "price", e.target.value)} data-testid={`pricing-price-${item.id}`} />
                  <button onClick={() => removeItem(si, ii)} data-testid={`pricing-remove-${item.id}`} aria-label="Remove item" className="shrink-0 text-[#F9F6F0]/40 hover:text-red-300 transition-colors p-2">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
            <button onClick={() => addItem(si)} data-testid={`pricing-add-${section.id}`} className="mt-4 inline-flex items-center gap-2 text-xs text-[#C5A059] border border-[#C5A059]/40 rounded-full px-4 py-2 hover:bg-[#C5A059]/10 transition-colors">
              <Plus size={13} /> Add item
            </button>
          </div>
        ))}
      </div>
      <button
        onClick={save}
        disabled={saving}
        data-testid="pricing-save-button"
        className="mt-8 px-10 py-4 rounded-full bg-[#8B5D6C] text-[#F9F6F0] text-sm font-medium border border-[#C5A059]/60 hover:bg-[#4A2834] disabled:opacity-60 transition-colors"
      >
        {saving ? "Saving…" : "Save pricing"}
      </button>
    </div>
  );
};

const AvailabilityTab = ({ blocked, reload }) => {
  const [date, setDate] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/availability", { date, note });
      toast.success("Date blocked — customers will see it as fully booked");
      setDate("");
      setNote("");
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/availability/${id}`);
      toast.success("Date reopened for orders");
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="availability-manager">
      <form onSubmit={add} className="lg:col-span-4 bg-[#281D22] border border-[#B87D8E]/15 rounded-2xl p-6 space-y-4 h-fit" data-testid="availability-form">
        <p className="font-serif text-2xl text-[#F9F6F0]">Block a date</p>
        <p className="text-xs text-[#F9F6F0]/40 leading-relaxed">
          Customers can't request a cake for dates you're fully booked — the order form will show them as unavailable.
        </p>
        <div>
          <label className="block text-xs uppercase tracking-[0.2em] text-[#B87D8E] mb-2">Date</label>
          <input type="date" required className="field-dark" value={date} onChange={(e) => setDate(e.target.value)} data-testid="availability-date-input" />
        </div>
        <input className="field-dark" placeholder="Note (optional) — e.g. Wedding weekend" value={note} onChange={(e) => setNote(e.target.value)} data-testid="availability-note-input" />
        <button
          type="submit"
          disabled={saving}
          data-testid="availability-add-button"
          className="w-full py-3.5 rounded-full bg-[#8B5D6C] text-[#F9F6F0] text-sm font-medium border border-[#C5A059]/60 hover:bg-[#4A2834] disabled:opacity-50 transition-colors"
        >
          {saving ? "Blocking…" : "Block this date"}
        </button>
      </form>

      <div className="lg:col-span-8">
        {blocked.length === 0 ? (
          <div className="text-center py-20 text-[#F9F6F0]/40 bg-[#281D22] border border-[#B87D8E]/15 rounded-2xl" data-testid="availability-empty">
            <CalendarOff className="mx-auto mb-4" size={32} />
            <p className="font-serif text-2xl">Every date is open</p>
            <p className="text-sm mt-2">Block a date when you're fully booked or away.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="availability-list">
            {blocked.map((b) => (
              <motion.div
                key={b.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center justify-between gap-4 bg-[#281D22] border border-[#B87D8E]/15 rounded-2xl px-6 py-4"
                data-testid={`availability-item-${b.id}`}
              >
                <div>
                  <p className="font-serif text-xl text-[#F9F6F0]">{fmtDate(b.date)}</p>
                  {b.note && <p className="text-xs text-[#F9F6F0]/50 mt-1">{b.note}</p>}
                </div>
                <button
                  onClick={() => remove(b.id)}
                  data-testid={`availability-remove-${b.id}`}
                  className="inline-flex items-center gap-2 text-xs text-red-300 border border-red-400/40 rounded-full px-4 py-2 hover:bg-red-500/10 transition-colors"
                >
                  <Trash2 size={13} /> Reopen
                </button>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [blocked, setBlocked] = useState([]);

  const reload = useCallback(() => {
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/admin/conversations").then((r) => setConversations(r.data)).catch(() => {});
    api.get("/gallery").then((r) => setGallery(r.data)).catch(() => {});
    api.get("/pricing").then((r) => setPricing(r.data)).catch(() => {});
    api.get("/availability").then((r) => setBlocked(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/auth/me").then(reload).catch(() => navigate("/admin"));
  }, [navigate, reload]);

  useEffect(() => {
    const id = setInterval(reload, 15000);
    return () => clearInterval(id);
  }, [reload]);

  const logout = () => {
    localStorage.removeItem("velvet_admin_token");
    navigate("/admin");
  };

  const unread = conversations.filter((c) => c.unread_admin).length;
  const pending = orders.filter((o) => o.status === "Pending").length;

  return (
    <div className="min-h-screen bg-[#1C1417] text-[#F9F6F0]" data-testid="admin-dashboard">
      <header className="border-b border-[#B87D8E]/15 sticky top-0 bg-[#1C1417]/90 backdrop-blur-md z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CakeMark className="w-9 h-9" />
            <div>
              <p className="font-serif text-xl leading-none">Velvet Bakes Studio</p>
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#B87D8E] mt-1">Welcome back, Lall</p>
            </div>
          </div>
          <button onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-2 text-xs text-[#F9F6F0]/50 hover:text-[#C5A059] transition-colors border border-[#B87D8E]/20 rounded-full px-4 py-2">
            <LogOut size={13} /> Sign out
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-wrap gap-2 mb-8" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`relative px-6 py-2.5 rounded-full text-sm tracking-wide transition-all border ${
                tab === t.id
                  ? "bg-[#8B5D6C] text-[#F9F6F0] border-[#C5A059]/60"
                  : "border-[#B87D8E]/20 text-[#F9F6F0]/60 hover:border-[#B87D8E]/50"
              }`}
            >
              {t.label}
              {t.id === "messages" && unread > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#C5A059] text-[#1C1417] text-[10px] font-bold flex items-center justify-center" data-testid="messages-unread-badge">
                  {unread}
                </span>
              )}
              {t.id === "orders" && pending > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#C5A059] text-[#1C1417] text-[10px] font-bold flex items-center justify-center" data-testid="orders-pending-badge">
                  {pending}
                </span>
              )}
            </button>
          ))}
        </div>

        {tab === "orders" && <OrdersTab orders={orders} reload={reload} />}
        {tab === "messages" && <MessagesTab conversations={conversations} reload={reload} />}
        {tab === "gallery" && <GalleryTab gallery={gallery} reload={reload} />}
        {tab === "pricing" && <PricingTab pricing={pricing} reload={reload} />}
        {tab === "availability" && <AvailabilityTab blocked={blocked} reload={reload} />}
      </div>
    </div>
  );
};

export default AdminDashboard;
